import { ViewIds } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  availableReferenceFieldOptions,
  FieldSource,
  getBubbleChartViewContext,
  getSelectedQuadrantDropdown,
  referenceTypeNameOption,
  toBubbleChartSettingsBounds,
  toReferenceTypeOptionList,
  type BubbleChartFieldNotifiers,
  type BubbleChartPopovers,
  type BubbleChartRenderingData,
  type BubbleChartSettingsBounds,
  type BubbleChartViewSettings,
  type FieldData,
  type FieldInfoKey,
  type FieldNameAndLabel,
  type FieldPickerArgs,
  type QuadrantZoomState,
} from '@ardoq/bubble-chart';
import { isTruthy } from '@ardoq/common-helpers';
import { isBoolean, isObject, pick } from 'lodash';
import { DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { ChartWindowState } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import {
  DropdownConfig,
  SettingsConfig,
  SettingsType,
  updateViewSettings,
  ViewContext,
} from '@ardoq/view-settings';
import { includeAllDescendantsDropdownMenu } from 'tabview/includeAllDescendantsDropdownMenu';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { viewLegendCommands } from '@ardoq/view-legend';
import {
  getBubbleSizeScaleSliderOption,
  getLayoutDropdownConfig,
  showBubblesWithZeroValueConfig,
  sortedFieldNameOptions,
} from '../viewSettings/viewSettingsUtils';
import {
  getAvailableReferenceFieldOptionsLabels,
  getSelectedQuadrantDropdownLabels,
  getTimeLayoutLabels,
} from 'locales/getBubbleChartLabels';

const VIEW_ID = ViewIds.BUBBLE;

export type WorkspaceModeBubbleChartSettingsControlsDependencies =
  BubbleChartSettingsBounds & {
    viewSettings: BubbleChartViewSettings;
    viewInstanceId: string;
    isEmptyView: boolean;
    isPresentationMode: boolean;
    numericFields: FieldData[];
    availableReferenceFields: FieldNameAndLabel[];
    availableReferencedComponentFields: FieldNameAndLabel[];
    availableIncomingReferenceTypeNames: string[];
    availableOutgoingReferenceTypeNames: string[];
    quadrantZoomState: QuadrantZoomState;
    windowState: ChartWindowState;
    locale: Locale;
    handles: {
      popovers: BubbleChartPopovers;
      fieldNotifiers?: BubbleChartFieldNotifiers;
      getBubbleChartContentContainer: () => HTMLDivElement | null;
    };
  };

export const toWorkspaceModeSettingsControlsDependencies = (
  state: BubbleChartRenderingData
): WorkspaceModeBubbleChartSettingsControlsDependencies => {
  const {
    viewSettings,
    data,
    isLoadingData,
    numericFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    quadrantZoomState,
    viewInstanceId,
    isPresentationMode,
    popovers,
    fieldNotifiers,
    locale,
  } = state;

  return {
    ...toBubbleChartSettingsBounds({
      viewSettings,
      data,
      numericFields,
      availableBubbleComponentFields: [],
      availableReferenceFields,
      availableReferencedComponentFields,
      componentTypeRegistry: null,
      isReferenceMode: viewSettings.fieldSource === FieldSource.BY_REFERENCE,
    }),
    viewSettings,
    viewInstanceId,
    isEmptyView: !isLoadingData && !data.length,
    isPresentationMode: Boolean(isPresentationMode),
    numericFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    quadrantZoomState,
    windowState: pick(state, ['windowCenter', 'windowScale']),
    locale,
    handles: {
      popovers,
      fieldNotifiers,
      getBubbleChartContentContainer: state.getBubbleChartContentContainer,
    },
  };
};

export const isWorkspaceModeBubbleChartSettingsControlsDependencies = (
  settingsControlsDependencies: Record<string, unknown>
): settingsControlsDependencies is WorkspaceModeBubbleChartSettingsControlsDependencies =>
  isObject(settingsControlsDependencies.viewSettings) &&
  isObject(settingsControlsDependencies.dimensionRanges) &&
  isObject(settingsControlsDependencies.handles) &&
  typeof settingsControlsDependencies.viewInstanceId === 'string' &&
  Array.isArray(settingsControlsDependencies.componentAxisFieldNames) &&
  Array.isArray(settingsControlsDependencies.labelFilterTargets) &&
  [
    settingsControlsDependencies.isReferenceMode,
    settingsControlsDependencies.hasFieldsInCurrentMode,
    settingsControlsDependencies.isPresentationMode,
    settingsControlsDependencies.isEmptyView,
  ].every(isBoolean);

export const toWorkspaceModeViewContext = ({
  viewSettings,
  viewInstanceId,
  handles: { getBubbleChartContentContainer },
}: WorkspaceModeBubbleChartSettingsControlsDependencies): ViewContext =>
  getBubbleChartViewContext({
    viewLegendCommands,
    getBubbleChartContentContainer,
    isLegendActive: viewSettings.isLegendActive,
    zoomControls: [],
    viewInstanceId,
  });

const FIELD_NOTIFIER_BY_DIMENSION = {
  selectedFieldNameX: 'xFieldDropdown',
  selectedFieldNameY: 'yFieldDropdown',
  selectedFieldNameRadius: 'bubbleSizeFieldDropdown',
} as const satisfies Record<FieldInfoKey, keyof BubbleChartFieldNotifiers>;

const getFieldPicker = (
  {
    numericFields,
    viewSettings,
    availableReferenceFields,
    availableReferencedComponentFields,
    isReferenceMode,
    hasFieldsInCurrentMode,
    locale,
    handles: { fieldNotifiers },
  }: WorkspaceModeBubbleChartSettingsControlsDependencies,
  i18nLocale: I18nLocale
) => {
  const fieldPickersDisabled = !hasFieldsInCurrentMode;

  return ({
    fieldName,
    additionalOptions = [],
    ...rest
  }: FieldPickerArgs): DropdownConfig => ({
    id: fieldName,
    options: [
      ...(isReferenceMode
        ? availableReferenceFieldOptions({
            availableBubbleComponentFields: [],
            availableReferenceFields,
            availableReferencedComponentFields,
            currentSettings: viewSettings,
            key: fieldName,
            locale,
            shouldClearLabelFilter: false,
            labelsProp: getAvailableReferenceFieldOptionsLabels(i18nLocale),
            resetFieldOnDeselect: false,
          })
        : sortedFieldNameOptions(locale, {
            viewId: VIEW_ID,
            list: numericFields,
            settingsKey: fieldName,
            isActive: name => viewSettings[fieldName] === name,
            onViewSettingsUpdate,
          })),
      ...additionalOptions,
    ],
    type: SettingsType.DROPDOWN,
    isDisabled: fieldPickersDisabled,
    popoverText: fieldPickersDisabled
      ? t(
          i18nLocale,
          'tabview:bubbleChart.fieldPickersDisabled',
          'There is no field in the selected field source.'
        )
      : undefined,
    requestOpen: fieldNotifiers?.[FIELD_NOTIFIER_BY_DIMENSION[fieldName]],
    ...rest,
  });
};

const getFieldSourceDropdown = (
  {
    viewSettings,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    isReferenceMode,
  }: WorkspaceModeBubbleChartSettingsControlsDependencies,
  i18nLocale: I18nLocale
): DropdownConfig => {
  const { selectedReferenceDirection, selectedReferenceTypeName } =
    viewSettings;

  const fieldSourceByReferenceIsDisabled =
    availableIncomingReferenceTypeNames.length +
      availableOutgoingReferenceTypeNames.length ===
    0;

  return {
    id: 'selectFieldSource',
    type: SettingsType.DROPDOWN,
    label: t(
      i18nLocale,
      'tabview:bubbleChart.selectFieldSource',
      'Select field source'
    ),
    iconName: IconName.SETTINGS,
    options: [
      {
        name: 'fieldSourceByComponents',
        type: DropdownOptionType.OPTION,
        label: t(
          i18nLocale,
          'tabview:bubbleChart.componentFieldsOnly',
          'Component fields only'
        ),
        isActive: viewSettings.fieldSource === FieldSource.BY_COMPONENT,
        onClick: () =>
          dispatchAction(
            updateViewSettings({
              viewId: VIEW_ID,
              settings: { fieldSource: FieldSource.BY_COMPONENT },
              persistent: true,
            })
          ),
      } satisfies DropdownOption,
      {
        name: 'fieldSourceByReference',
        type: DropdownOptionType.SUBMENU,
        label: t(
          i18nLocale,
          'tabview:bubbleChart.referenceFields',
          'Reference fields'
        ),
        isActive: viewSettings.fieldSource === FieldSource.BY_REFERENCE,
        isDisabled: fieldSourceByReferenceIsDisabled,
        description: t(
          i18nLocale,
          'tabview:bubbleChart.referenceFieldsDescription',
          'This will also include component fields linked to the selected reference type'
        ),
        popoverProps: fieldSourceByReferenceIsDisabled
          ? {
              content: t(
                i18nLocale,
                'tabview:bubbleChart.noReferenceInContext',
                'There is no reference in the selected context components or workspace.'
              ),
            }
          : undefined,
        maxDropdownSize: DropdownSize.M,
        options: toReferenceTypeOptionList({
          availableIncomingReferenceTypeNames,
          availableOutgoingReferenceTypeNames,
          labels: {
            incomingHeader: t(
              i18nLocale,
              'tabview:bubbleChart.incoming',
              'Incoming'
            ),
            outgoingHeader: t(
              i18nLocale,
              'tabview:bubbleChart.outgoing',
              'Outgoing'
            ),
          },
          toOption: (referenceTypeName, direction) =>
            referenceTypeNameOption(
              referenceTypeName,
              direction,
              isReferenceMode &&
                selectedReferenceDirection === direction &&
                selectedReferenceTypeName === referenceTypeName
            ),
        }),
      },
    ],
  };
};

type ToSettingsControlsArgs = {
  dependencies: WorkspaceModeBubbleChartSettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

export const toWorkspaceModeViewModifiers = ({
  dependencies,
  i18nLocale,
}: ToSettingsControlsArgs): SettingsConfig[] => {
  if (dependencies.isPresentationMode) {
    return [];
  }

  const {
    viewSettings,
    quadrantZoomState,
    windowState,
    viewInstanceId,
    handles: { popovers },
  } = dependencies;

  const fieldPicker = getFieldPicker(dependencies, i18nLocale);

  return [
    getFieldSourceDropdown(dependencies, i18nLocale),
    fieldPicker({
      fieldName: 'selectedFieldNameX',
      label: t(
        i18nLocale,
        'tabview:bubbleChart.selectXAxisField',
        'Select X-axis field'
      ),
      iconName: IconName.ARROW_RIGHT_ALT,
    }),
    fieldPicker({
      fieldName: 'selectedFieldNameY',
      label: t(
        i18nLocale,
        'tabview:bubbleChart.selectYAxisField',
        'Select Y-axis field'
      ),
      iconName: IconName.ARROW_TOP_ALT,
    }),
    fieldPicker({
      fieldName: 'selectedFieldNameRadius',
      label: t(
        i18nLocale,
        'tabview:bubbleChart.selectSizeField',
        'Select Size field'
      ),
      iconName: IconName.BUBBLE_CHART,
      additionalOptions: [
        { type: DropdownOptionType.DIVIDER },
        getBubbleSizeScaleSliderOption(viewSettings, i18nLocale),
      ],
    }),
    getLayoutDropdownConfig(
      viewSettings,
      i18nLocale,
      popovers.disabledZoomToQuadrantPopover.selectBackgroundNotifier
    ),
    getSelectedQuadrantDropdown(
      viewSettings,
      quadrantZoomState,
      viewInstanceId,
      windowState,
      popovers.disabledZoomToQuadrantPopover.popoverContent,
      getSelectedQuadrantDropdownLabels(i18nLocale),
      getTimeLayoutLabels(i18nLocale)
    ),
    includeAllDescendantsDropdownMenu({
      includeAllDescendants: viewSettings.includeAllDescendants,
      i18nLocale,
    }),
    showBubblesWithZeroValueConfig(viewSettings, i18nLocale),
  ].filter(isTruthy);
};
